import type { TextMarker } from '@/shared/lib';
import { STORED_MESSAGE_MARK_PATTERN } from '../lib/commentMessageMarks';
import { STORED_TIMECODE_PATTERN } from '../lib/commentTimecodes';

/** Ссылка на правило в тексте комментария. */
export type RuleMarker = { type: 'rule'; id: number; text: string };

/** Ссылка на узел знаний в тексте комментария. */
export type KnowledgeNodeMarker = { type: 'knowledgeNode'; id: number };

/** Таймкод фрагмента записи в тексте комментария. */
export type TimeFragmentMarker = { type: 'fragment'; time: string };

/** Метка места в переписке текстовой коммуникации в тексте комментария. */
export type MessageMarker = { type: 'message'; sentAt: string };

/** Служебный маркер в тексте комментария к оценке. */
export type CommentMarker = RuleMarker | KnowledgeNodeMarker | TimeFragmentMarker | MessageMarker;

/** Служебный маркер в тексте комментария к атрибуту. */
export type AttrCommentMarker = TimeFragmentMarker | MessageMarker;

/** Ссылка на правило: `[ruleId: 12|Название правила]`. */
const RULE_MARKER: TextMarker<RuleMarker> = {
  pattern: /\[ruleId:\s*(\d+)\|([^\]]+)\]/,
  parse: (match) => ({ type: 'rule', id: Number(match[1]), text: match[2] }),
};

/** Ссылка на узел знаний: `[knowledgeNodeId: 7]`. */
const KNOWLEDGE_NODE_MARKER: TextMarker<KnowledgeNodeMarker> = {
  pattern: /\[knowledgeNodeId:\s*(\d+)\]/,
  parse: (match) => ({ type: 'knowledgeNode', id: Number(match[1]) }),
};

/** Собирает ссылку на узел знаний для вставки в текст комментария. */
export const buildKnowledgeNodeMarker = (nodeId: number): string => `[knowledgeNodeId:${nodeId}]`;

/** Таймкод фрагмента записи: `[12:34]` или `[1:23:45]`. */
const TIME_FRAGMENT_MARKER: TextMarker<TimeFragmentMarker> = {
  pattern: STORED_TIMECODE_PATTERN,
  parse: (match) => ({ type: 'fragment', time: match[1] }),
};

/** Метка места в переписке: `[28.12.2024 14:53]` — минута отправки элемента в UTC. */
const MESSAGE_MARKER: TextMarker<MessageMarker> = {
  pattern: STORED_MESSAGE_MARK_PATTERN,
  parse: (match) => ({ type: 'message', sentAt: match[1] }),
};

/** Маркеры комментария к оценке: ссылки на правила, узлы знаний, таймкоды и метки места в переписке. */
export const EVALUATION_COMMENT_MARKERS: TextMarker<CommentMarker>[] = [
  RULE_MARKER,
  KNOWLEDGE_NODE_MARKER,
  TIME_FRAGMENT_MARKER,
  MESSAGE_MARKER,
];

/** Маркеры комментария к атрибуту: таймкоды и метки места в переписке. */
export const ATTR_COMMENT_MARKERS: TextMarker<AttrCommentMarker>[] = [TIME_FRAGMENT_MARKER, MESSAGE_MARKER];
